/**
 * Waffle flags should be added to this list before usage in JS/TS.
 * Each entry should:
 *   - Be given a descriptive key name
 *   - Include a description of what the flag gates
 *   - Link to a ticket for flag removal, or an explanation as to why there isn't one
 *
 * Note: Though this enum can (and should!) be used from JS/Ractive, its usage is only
 * _enforced_ in TS code, so it's still recommended to search for both the enum AND the
 * "raw" waffle flag string for removal, unless you are sure you know otherwise.
 */
enum WaffleFlag {
  // Enables an emoji picker add-on to the composer (no removal ticket known)
  EmojiPicker = 'emoji-picker-widget',
  // Allows us to set a base URL for static lists API that is on a different
  // domain (WEB-888)
  StaticListsCORS = 'static-lists-cors-flag',
  // Hides legacy rich push editor unless an app has custom Rich Push templates. (removal: TPS-689)
  HideLegacyRichPushEditorUnlessAppHasCustomTemplates = 'hide-legacy-rich-push-editor',
  // Enables Generic pass type for google wallet (removal: CHAN-4603)
  GoogleGeneric = 'google-generic-flag',
  // Enable Google Template Overrides for Loyalty Passes (removal: CHAN-4429)
  LoyaltyOverrides = 'google-overrides-loyalty-flag',
  // Enable Channel coordination in the builder (removal : ADVSEG-1027)
  BuilderChannelCoordination = 'builder-channel-coordination-flag',
  // Generated reactivation exits for reactivation journeys (ORC-2344)
  GeneratedReactivationCancellationExits = 'generated-reactivation-cancellation',
  // Enable Push local time for A/B testing (removal : ADVSEG-1815)
  PushLocalTimeForExperiment = 'push-local-time-for-experiment-flag',
  // Enable throttling for A/B testing (removal : ADVSEG-1894)
  ThrottlingForExperiment = 'throttling-for-experiment-flag',
  // SMS Retargeting (Removal: CHAN-3806)
  SMSRetargeting = 'sms-retargeting',
  // EEnable new design for the global create popup (removal : ADVSEG-1968)
  GlobalCreateReworkFlag = 'global-create-rework-flag',
  // New app settings dashboard
  ControlPanelDarkMatterSms = 'control-panel-dark-matter-sms',
  // SFTP Configuration in APIs & Integrations
  SftpConfiguration = 'sftp-configuration-flag',
  // Enables doing A/B tests on delays between messages in journeys. (removal: ORC-2679)
  JourneyABTestDelay = 'journey-ab-test-delay',

  // Enable NEW ACTIONS IN  WYSIWYG (removal : ADVSEG-2272)
  ExtraActionsInWYSIWYG = 'extra-actions-in-wysiwyg-flag',

  // Enable contact checkbox in feedback modal (removal: ADVSEG-2362)
  ContactCheckBoxFeedbackFlag = 'contact-checkbox-feedback-flag',

  // Bulk add/remove tags (removal: CHAN-4392)
  ContactManagementTagUpload = 'contact-management-tag-upload-flag',

  // Contact Management Named User messages embedded looker sso url
  // (removal CHAN-4620)
  NamedUserMessagesDemo = 'named-user-messages-demo-flag',

  // Scene AB Testing (Removal: ADVSEG-3687)
  SceneABTestFlag = 'scene-ab-test-flag',

  // Enabled optimal time selection for journey recurring schedules (removal: ORC-3741)
  JourneyRecurringSchedulesOptimalTime = 'journey-recurring-schedule-optimal-time',

  // Enables optimal time schedule "trigger" selection (removal: ORC-3748)
  JourneySchedulesOptimalTime = 'journey-schedule-optimal-time',

  // Add a way to select the font size in pt in scenes & surveys (removal: ADVSEG-3885)
  PixelToPointFontSizeAXP = 'pixel-to-point-axp-flag',

  // Enables scenes Content improvements (removal: ADVSEG-3887)
  ScenesContentImprovements = 'scenes-content-improvements',

  // Enables the temporary contact migration selector in preference centers (removal: TPS-3280)
  PreferenceCenterEnableContactMigrationSelector = 'preference-center-enable-contact-migration-selector',

  // Moves the set a tag on message display to behavior page in IAAs (removal: ADVSEG-3970)
  MoveSetTagToBehaviorPageFlag = 'move-set-tag-to-behavior-page-flag',

  // Enables cross-channel segmentation UI additionsa (removal: TPS-3257)
  CrossChannelSegmentation = 'cross-channel-segmentation',

  // Enables creating Sequence schedules from customer dates (removal: ORC-3844)
  JourneyScheduleCustomerDate = 'journey-schedule-customer-date-flag',

  // Enables Managing downstream connections improvements (removal: ORC-3906)
  ManagingDownstreamConnections = 'managing-downstream-connections',

  //   SMS workflows v1.1 (removal: CHAN-4864)
  SmsWorkflowsV1FastFollow = 'sms-workflows-v1-fast-follow',

  // Enables migrating legacy channel level to user level preference centers (removal: TPS-3407)
  LegacyPreferenceCenterMigration = 'legacy-preference-center-migration',

  // Enables the string (is not) compatoator selector for event properties (removal: ORC-3950)
  CustomEventPropertyStringIsNot = 'custom-event-property-string-is-not',
  // ^^^ Add new flags above ^^^
  // Be sure to add new flags to airship/polybius/flags.py, otherwise they
  // will not be created by the sync_flags management command!
}

export default WaffleFlag
